/*global payhere*/
import React, { useEffect, useState } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TermsPopup from './TermsPopup';
import { useAuthenticatedApi, subscriptionApi, paymentApi } from '../../../utils/api';

const DurationStr = {
  '1 Year': 'Annual',
  '1 Month': 'Monthly',
  '1 Day': 'Day Pass',
};

const PaymentButton = ({ DurationTime, userType }) => {
  const { state, signIn, getBasicUserInfo, getDecodedIDToken } = useAuthContext();
  const { makeAuthenticatedRequest } = useAuthenticatedApi();
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script); // Cleanup
    };
  }, []);
  
  const handlePayment = async () => {
    if (isProcessing) return; // Prevent double-clicks
    
    try {
      setIsProcessing(true);

      // Step 1: Check Authentication
      if (!state.isAuthenticated) {
        const currentPath = window.location.pathname;
        sessionStorage.setItem('redirectAfterLogin', currentPath);
        await signIn();
        return;
      }

      // Step 2: Fetch User Info
      const userInfo = await getBasicUserInfo();
      const moreInfo = await getDecodedIDToken();
      const email = userInfo.email || moreInfo.email;

      if (!email) {
        throw new Error('User email is missing.');
      }

      console.log('Processing payment for user:', email);

      // Step 3: Check if the user is already paid
      const plan = DurationStr[DurationTime];

      try {
        const subscriptionData = await subscriptionApi.get(email, makeAuthenticatedRequest);
        
        if (subscriptionData.status === 'not_found') {
          console.log('Subscription not found for the user.');
        } else if (subscriptionData.ispaid) {
          console.log('Subscription already paid.');
          toast.warning('You have already paid for this subscription.');
          return;
        } else {
          console.log('Subscription expired.');
        }
      } catch (error) {
        if (error.message.includes('Authentication failed')) {
          toast.error('Authentication failed. Please login again.');
          await signIn();
          return;
        }
        console.error('Subscription check error:', error);
        // Continue with payment even if subscription check fails
      }

      // Step 4: Generate Payment Hash
      const paymentDetails = {
        order_id: email,
        amount:
          plan === 'Annual'
            ? '24000.00'
            : plan === 'Monthly'
            ? '2500.00'
            : '500.00',
        currency: 'LKR',
        first_name: userInfo.givenName || moreInfo.given_name || '',
        last_name: userInfo.familyName || moreInfo.family_name || '',
        email: email,
        phone: '',
        address: '',
        city: '',
        country: 'Sri Lanka',
        custom_1: userType,
        custom_2: plan,
      };

      console.log('Generating payment hash for:', paymentDetails);

      const { hash, merchant_id } = await paymentApi.start(paymentDetails, makeAuthenticatedRequest);

      console.log('Payment hash generated successfully');

      // Step 5: Initialize Payment
      const payment = {
        sandbox: import.meta.env.VITE_SANDBOX,
        merchant_id: merchant_id,
        return_url: import.meta.env.VITE_RETURN_URL,
        cancel_url: import.meta.env.VITE_CANCEL_URL,
        notify_url: import.meta.env.VITE_NOTIFY_URL,
        order_id: paymentDetails.order_id,
        items: `${plan} Subscription`,
        amount: paymentDetails.amount,
        currency: paymentDetails.currency,
        first_name: paymentDetails.first_name,
        last_name: paymentDetails.last_name,
        email: paymentDetails.email,
        phone: paymentDetails.phone,
        address: paymentDetails.address,
        city: paymentDetails.city,
        country: paymentDetails.country,
        hash: hash,
        custom_1: paymentDetails.custom_1,
        custom_2: paymentDetails.custom_2,
      };

      if (!acceptedTerms) {
        setShowTerms(true);
        return;
      }

      console.log('Starting PayHere payment...');
      payhere.startPayment(payment);

    } catch (error) {
      console.error('Payment process error:', error);
      
      if (error.message.includes('Authentication failed')) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.message.includes('Access denied')) {
        toast.error('Access denied. Please check your permissions.');
      } else {
        toast.error('Payment initialization failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (acceptedTerms) {
      handlePayment();
    }
  }, [acceptedTerms]);

  return (
    <>
      <ToastContainer position="bottom-right" autoClose={5000} />
      <button
        onClick={handlePayment}
        disabled={isProcessing}
        className={`text-white transition duration-500 ease-in-out rounded-25 text-18 px-15 h-50 ${
          isProcessing
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-pl-accent hover:bg-pl-accent-hover'
        }`}
      >
        {isProcessing ? 'Processing...' : `Choose ${DurationStr[DurationTime]} Plan`}
      </button>

      {showTerms && (
        <TermsPopup
          onAccept={() => {
            setAcceptedTerms(true);
            setShowTerms(false);
          }}
          onClose={() => setShowTerms(false)}
        />
      )}
    </>
  );
};

export default PaymentButton;